"use client";
import React, { cloneElement, ReactElement } from "react";
import { useState, useEffect, useRef, Children } from "react";

export interface DropdownProps {
  title: string;
  children: React.ReactNode;
}

const Dropdown: React.FC<DropdownProps> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<string>("512px");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Toggle dropdown visibility
  const toggleDropdown = () => setIsOpen((prev) => !prev);

  // Close dropdown when clicking outside
  const handleOutsideClick = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Add event listener to detect clicks outside the dropdown
    document.addEventListener("mousedown", handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const screenHeight = window.innerHeight;
      const spaceBelow = screenHeight - buttonRect.bottom;
      let maxHeight = 521;
      if (spaceBelow - 20 < 512) maxHeight = spaceBelow - 20;
      setDropdownMaxHeight(`${maxHeight}px`);
    }
  }, [isOpen]);

  // Handle item click (close dropdown)
  const handleItemClick = () => {
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative z-[1] inline-block text-left">
      {/* Dropdown Button */}
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className={`inline-flex items-center px-2 py-5 text-sm font-medium uppercase hover:bg-tertiary-3 ${isOpen ? "bg-tertiary-3" : ""}`}
      >
        {title}
        <svg
          className={`ms-2 h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu max-w-max */}
      {isOpen && (
        <div
          className="absolute w-max min-w-64 origin-top-right overflow-y-auto bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] ltr:left-0 rtl:right-0"
          style={{
            maxHeight: dropdownMaxHeight,
          }}
        >
          {Children.map(children, (child, index) =>
            React.isValidElement(child)
              ? cloneElement(child as ReactElement, { key: index, onClose: handleItemClick })
              : child,
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
